export const useNotifications = () => {
  const page = ref(1);
  const pageSize = ref(4);
  const maxPages = ref(0);
  const isFetching = ref(false);
  const userStore = useUserStore();
  const readAllPromise = ref<Promise<void> | null>(null);
  const {
    getNotifications,
    setNotifications,
    setReadNotification,
    setReadAllNotifications,
    getUnreadNotificationsCount,
    setUnreadNotificationsCount,
  } = useUserController();
  const { checkAndGetMerchantTids } = useAppStateStore();

  const fetchNotifications = async () => {
    if (!isFetching.value) {
      isFetching.value = true;
      const res = await getNotifications(1, pageSize.value);
      setNotifications(res);
      if (res.payload) {
        await getNotificationTids(res.payload);
        maxPages.value = userStore.notifications.total / pageSize.value;
      }
      isFetching.value = false;
      if (readAllPromise.value) await readAllPromise.value;
    }
  };

  const getNotificationTids = async (notifications: Notifications) => {
    const merchantIds = [
      ...new Set(
        notifications.records.map(
          (notification) => notification.translationId.split(".")[0]
        )
      ),
    ] as MerchantId[];
    await Promise.all(
      merchantIds.map((merchantId) => checkAndGetMerchantTids(merchantId))
    );
  };

  const fetchMore = async () => {
    if (!isFetching.value) {
      if (page.value > maxPages.value) {
        return;
      }
      isFetching.value = true;
      page.value += 1;
      const res = await getNotifications(page.value, pageSize.value);
      if (res.payload) {
        await getNotificationTids(res.payload);
        userStore.notifications.records = [
          ...(userStore.notifications.records || []),
          ...res.payload.records,
        ];
        userStore.notifications.unread = res.payload.unread;
      }
      isFetching.value = false;
      if (readAllPromise.value) await readAllPromise.value;
    }
  };

  const readSelected = (id: string) => {
    setReadNotification(id);
    userStore.notifications.records?.find((notification: INotification) => {
      if (notification.id === id) {
        notification.read = true;
      }
    });
    if (userStore.notifications.unread > 0) userStore.notifications.unread -= 1;
  };

  const readAll = async () => {
    if (!isFetching.value) {
      await setReadAllNotifications();
      userStore.notifications.unread = 0;
      userStore.notifications.records.forEach((notification: INotification) => {
        notification.read = true;
      });
      readAllPromise.value = null;
    } else {
      readAllPromise.value = new Promise(async (resolve) => {
        resolve();
        await setReadAllNotifications();
        userStore.notifications.unread = 0;
        userStore.notifications.records.forEach(
          (notification: INotification) => {
            notification.read = true;
          }
        );
      });
    }
  };

  const updateUnreadNotificationsCount = async () => {
    if (userStore.isLoggedIn && !isFetching.value) {
      isFetching.value = true;
      const res = await getUnreadNotificationsCount();
      isFetching.value = false;
      if (readAllPromise.value) await readAllPromise.value;
      if (res.payload?.unread === userStore.notifications.unread) return;
      if (res.payload?.unread != userStore.notifications.unread) {
        setUnreadNotificationsCount(res.payload?.unread || 0);
        if (userStore.notifications.unread == 0) {
          userStore.notifications.records.forEach(
            (notification: INotification) => {
              notification.read = true;
            }
          );
        }
      }
    }
  };

  return {
    pageSize,
    isFetching,
    fetchNotifications,
    fetchMore,
    readSelected,
    readAll,
    updateUnreadNotificationsCount,
  };
};
